import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSent(true);
        toast.success('Reset link sent');
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Could not reach server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center p-6" data-testid="forgot-password-success">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-[#8BAA9E]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#8BAA9E]" />
          </div>
          <h1 className="font-serif text-2xl text-[#F5F3EF] mb-3">Check your email</h1>
          <p className="text-[#B5B1AB] font-sans text-sm mb-8 leading-relaxed">
            If an account with <span className="text-[#DCD3CA]">{email}</span> exists, we've sent a password reset link. Check your inbox and spam folder.
          </p>
          <Link to="/login">
            <Button
              className="w-full bg-[#8BAA9E] hover:bg-[#7A9A8E] text-[#1C1C1E] rounded-full py-6 font-sans"
              data-testid="back-to-login-btn"
            >
              Back to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <Link to="/login" className="flex items-center gap-2 text-[#8BAA9E] text-sm font-sans mb-8 hover:text-[#A3C2B6] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </Link>

        <h1 className="font-serif text-2xl text-[#F5F3EF] mb-2">Reset your password</h1>
        <p className="text-[#B5B1AB] font-sans text-sm mb-8">
          Enter the email address associated with your account and we'll send you a link to reset your password.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#252528] border-white/10 rounded-xl py-6 px-4 text-[#F5F3EF] placeholder-[#7A7670] focus:border-[#8BAA9E]"
            data-testid="reset-email-input"
            required
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#8BAA9E] hover:bg-[#7A9A8E] text-[#1C1C1E] rounded-full py-6 font-sans disabled:opacity-50"
            data-testid="send-reset-link-btn"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
